/**
 * Hook to wrap react-oidc-context useAuth hook
 */

import {useAuth, type ErrorContext} from 'react-oidc-context';
import {useCallback, useMemo} from 'react';
import type {SigninRedirectArgs, SignoutRedirectArgs} from 'oidc-client-ts';
import {EHSAuthUser} from '../Types/EHSAuthUser';

type EHSAuthType = {
  getUser: () => EHSAuthUser;
  user: EHSAuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  signinRedirect: (args?: SigninRedirectArgs) => Promise<void>;
  signoutRedirect: (args?: SignoutRedirectArgs) => Promise<void>;
  hasRole: (role: string) => boolean;
  hasAnyRole: (requiredRoles?: string[]) => boolean;
  hasAllRoles: (requiredRoles?: string[]) => boolean;
  authError?: ErrorContext;
};
const useEHSAuth = (): EHSAuthType => {
  const auth = useAuth();

  const user = useMemo(() => (auth.user as EHSAuthUser) ?? null, [auth.user]);
  const roles = useMemo(() => user?.profile?.roles ?? [], [user]);
  const rolesSet = useMemo(() => new Set(roles), [roles]);

  const getUser = (): EHSAuthUser => {
    let user = auth.user;
    return user as EHSAuthUser;
  };

  const hasRole = useCallback((role: string) => rolesSet.has(role), [rolesSet]);

  const hasAnyRole = useCallback(
    (requiredRoles: string[] = []) => {
      if (!requiredRoles.length) return true; // no restriction
      return requiredRoles.some(r => rolesSet.has(r));
    },
    [rolesSet],
  );

  const hasAllRoles = useCallback(
    (requiredRoles: string[] = []) => {
      if (!requiredRoles.length) return true; // no restriction
      return requiredRoles.every(r => rolesSet.has(r));
    },
    [rolesSet],
  );

  return {
    getUser,
    user,
    isAuthenticated: auth.isAuthenticated,
    isLoading: auth.isLoading,
    signinRedirect: auth.signinRedirect,
    signoutRedirect: auth.signoutRedirect,
    hasRole,
    hasAnyRole,
    hasAllRoles,
    authError: auth.error,
  };
};

export default useEHSAuth;
